// @mui material components

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDAvatar from "components/MDAvatar";


import CrudService from "services/cruds-service";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import { subject } from "@casl/ability";

import getId from "services/helper-service";
import { appConfirm } from "components/AppDialog";
import Add from "@mui/icons-material/Add";
import {
  ListPage,
  CrmActionButton,
  CrmNotice,
  CrmRowActions,
  ListLink,
  ListText,
  ListStatus,
} from "layouts/ecommerce/products/new-product/crm/shared/CrmUi";

function UserManagement() {
  let { state } = useLocation();
  const [isDemo, setIsDemo] = useState(false);
  const [user, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [notification, setNotification] = useState({
    value: false,
    text: "",
  });

  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await CrudService.getUsers();
      setUsers(response.data);
      setRoles(response.included);
      setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
    })();
  }, []);

  useEffect(() => {
    if (!state) return;
    setNotification({
      value: state.value,
      text: state.text,
    });
  }, [state]);

  useEffect(() => {
    setTableData(getRows(user));
  }, [user]);

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: "",
        });
      }, 5000);
    }
  }, [notification]);

  const clickAddHandler = () => {
    navigate("/user-management/new-user");
  };

  const clickEditHandler = (id) => {
    navigate(`/user-management/edit-user/${id}`);
  };

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!await appConfirm("Are you sure you want to delete this user?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {
        await CrudService.deleteUser(id);
        // the delete does not send a response
        // so I need to get again the tags to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
        const response = await CrudService.getUsers();
        setUsers(response.data);
        setNotification({
          value: true,
          text: "The user has been successfully deleted",
        });
      }
    } catch (err) {
      // it sends error is the category is associated with an item
      console.error(err);
      if (err.hasOwnProperty("errors")) {
        setNotification({
          value: true,
          text: err.errors[0].title,
        });
      }
      return null;
    }
  };

  const getRows = (info) => {
    return info.map((row) => {
      let roleId = row.relationships.roles.data[0].id;
      let roleName = roles.find((role) => role.id == roleId);
      return {
        type: "users",
        id: row.id,
        image: row.attributes.profile_image,
        name: row.attributes.name,
        email: row.attributes.email,
        role: roleName?.attributes?.name || "N/A",
        title: row.attributes.title || "-",
        birthday: row.attributes.birthday
            ? new Date(row.attributes.birthday).toLocaleDateString()
            : "-",
        start_raw: row.attributes.start || null,
        start: row.attributes.start
            ? new Date(row.attributes.start).toLocaleDateString()
            : "-",

        created_at: new Date(row.attributes.created_at).toLocaleDateString(),
      };
    });
  };

  const dataTableData = {
    columns: [
      {
        Header: () => <span>user</span>,
        accessor: "name",
        Cell: ({ row }) => (
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <MDAvatar src={row.original.image} alt={row.original.name} size="sm" />
              <ListText
                  primary={<ListLink onClick={() => clickEditHandler(row.original.id)}>{row.original.name}</ListLink>}
                  secondary={row.original.email}
              />
            </MDBox>
        ),
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>role</span>,
        accessor: "role",
        width: "15%",
        Cell: ({ value }) => <ListStatus label={value} tone={/admin/i.test(value || "") ? "purple" : "navy"} />,
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>title</span>,
        accessor: "title",
        width: "15%",
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>birthday</span>,
        accessor: "birthday",
        width: "15%",
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>start</span>,
        accessor: "start",
        width: "15%",
        Cell: ({ row }) => {
          const rawStart = row?.original?.start_raw;
          if (!rawStart) return "-";

          const startDate = new Date(rawStart);

          const today = new Date();
          const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

          const diffDays = Math.max(
              0,
              Math.floor((todayDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24))
          );

          return (
              <ListText primary={startDate.toLocaleDateString()} secondary={`${diffDays} days`} />
          );
        },
      },


      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>created at</span>,
        accessor: "created_at",
        width: "15%",
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>actions</span>,
        disableSortBy: true,
        accessor: "actions",
        Cell: (info) => {
          const row = info.cell.row.original;
          // Demo modunda ilk üç kullanıcı korunur
          const isProtected = isDemo && ["1", "2", "3"].includes(String(getId(row.id)));
          return (
              <CrmRowActions
                  onEdit={!isProtected && ability.can("edit", subject("users", row)) ? () => clickEditHandler(row.id) : undefined}
                  onDelete={!isProtected && ability.can("delete", subject("users", row)) ? (e) => clickDeleteHandler(e, row.id) : undefined}
                  editTitle="Edit user"
                  deleteTitle="Delete user"
              />
          );
        },
      },
      ,
    ],

    rows: tableData,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ListPage
          title="User Management"
          subtitle="Team members with their roles and start dates. Open a user to edit their profile and permissions."
          actions={
            ability.can("create", "users") && (
                <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                  New User
                </CrmActionButton>
            )
          }
          notice={notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
          tableTitle="User list"
          tableSummary={`${tableData.length} users`}
      >
        <DataTable table={dataTableData} canSearch />
      </ListPage>
      <Footer />
    </DashboardLayout>
  );
}

export default UserManagement;
